import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Fade,
} from '@mui/material';
import {
  SearchOff,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

export interface NotFoundData {
  data: null | {
    api_status: string;
    api_message: string;
    operation_details?: {
      entity_type: string;
      identifier: string;
      missing_fields?: string[];
      error_details?: string;
    };
  };
  resumo: string;
  url: string;
  mensagem?: string;
  opcoes?: {
    id: number;
    descricao: string;
  }[];
}

export interface NotFoundViewProps {
  notFoundData: NotFoundData;
  onRetry?: () => void;
}

const NotFoundView: React.FC<NotFoundViewProps> = ({
  notFoundData,
  onRetry,
}) => {
  const { t } = useTranslation(['farm', 'common']);
  const { resumo } = notFoundData;

  // Extract the type of resource and name from the resumo or operation_details
  const extractInfo = (resumoText: string, operationDetails?: any) => {
    // Check if we have operation_details from error response
    if (operationDetails?.entity_type && operationDetails?.identifier) {
      const entityType = operationDetails.entity_type;
      const identifier = operationDetails.identifier;
      
      if (entityType === 'animal') {
        return {
          resourceType: t('farm:animal.title'),
          resourceName: identifier,
          icon: '🐄',
          suggestion: t('farm:notFound.animalSuggestion')
        };
      }
      
      if (entityType === 'lote') {
        return {
          resourceType: t('farm:lot.title'),
          resourceName: identifier,
          icon: '📦',
          suggestion: t('farm:notFound.lotSuggestion')
        };
      }
    }
    
    // Fallback to regex matching from resumo
    const loteMatch = resumoText.match(/Lote '([^']+)' não encontrado/);
    const animalMatch = resumoText.match(/[Aa]nimal '([^']+)' não foi encontrado|[Oo] animal '([^']+)' não foi encontrado/);
    
    if (loteMatch) {
      return {
        resourceType: t('farm:lot.title'),
        resourceName: loteMatch[1],
        icon: '📦',
        suggestion: t('farm:notFound.lotSuggestion')
      };
    }
    
    if (animalMatch) {
      const name = animalMatch[1] || animalMatch[2];
      return {
        resourceType: t('farm:animal.title'),
        resourceName: name,
        icon: '🐄',
        suggestion: t('farm:notFound.animalSuggestion')
      };
    }
    
    // Generic fallback
    return {
      resourceType: t('farm:notFound.resource'),
      resourceName: t('farm:notFound.requested'),
      icon: '🔍',
      suggestion: t('farm:notFound.genericSuggestion')
    };
  };

  const { resourceType, resourceName, icon, suggestion } = extractInfo(
    resumo || '', 
    notFoundData.data?.operation_details
  );
  
  // Get message to display (prefer mensagem over resumo)
  const displayMessage = notFoundData.mensagem || resumo || t('farm:notFound.defaultMessage', { resourceType, resourceName });
  
  // Check if we have options to display
  const hasOptions = notFoundData.opcoes && notFoundData.opcoes.length > 0;

  return (
    <Box sx={{ 
      p: 4, 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      minHeight: '400px',
      textAlign: 'center'
    }}>
      <Fade in timeout={600}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 500 }}>
          {/* Not Found Icon */}
          <Avatar
            sx={{
              width: 120,
              height: 120,
              bgcolor: DESIGN_TOKENS.colors.brand.primary,
              mb: 3,
              boxShadow: `0 8px 32px ${DESIGN_TOKENS.colors.brand.surface}`,
            }}
          >
            <SearchOff sx={{ fontSize: '4rem', color: 'white' }} />
          </Avatar>

          {/* Not Found Message */}
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              color: DESIGN_TOKENS.colors.brand.primary,
              mb: 2
            }}
          >
            {t('farm:common.notFound')}
          </Typography>

          <Typography 
            variant="h6" 
            sx={{ 
              color: DESIGN_TOKENS.colors.text.primary,
              mb: 1,
              fontWeight: 600
            }}
          >
            {displayMessage}
          </Typography>

          {/* Options */}
          {hasOptions && (
            <Box sx={{ mt: 3, maxWidth: 450 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: DESIGN_TOKENS.colors.text.primary,
                  fontWeight: 600,
                  mb: 2,
                  textAlign: 'center'
                }}
              >
                {t('farm:notFound.availableOptions')}
              </Typography>
              {notFoundData.opcoes!.map((opcao) => (
                <Box 
                  key={opcao.id}
                  sx={{ 
                    p: 2, 
                    mb: 1,
                    border: `1px solid ${DESIGN_TOKENS.colors.brand.surface}`,
                    borderRadius: 2,
                    bgcolor: DESIGN_TOKENS.colors.background.paper,
                    '&:hover': {
                      bgcolor: DESIGN_TOKENS.colors.brand.surface,
                      cursor: 'pointer'
                    }
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: DESIGN_TOKENS.colors.text.primary,
                      fontWeight: 500
                    }}
                  >
                    {opcao.id}. {opcao.descricao}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Info Text */}
          <Typography 
            variant="body2" 
            sx={{ 
              color: DESIGN_TOKENS.colors.text.secondary,
              mt: 3,
              maxWidth: 400,
              textAlign: 'center'
            }}
          >
            {suggestion}
          </Typography>
        </Box>
      </Fade>
    </Box>
  );
};

export default NotFoundView;